import React from 'react';

import {
  SOCIAL_BUTTON_WORDS,
  SOCIAL_PROVIDER_NAMES,
} from '@personal-trainer-app/shared';

import type { SocialOAuthButtonsProps } from './SocialOAuthButtons.types';
import type { SocialProvider } from '@personal-trainer-app/shared';

import { SocialProviderIcon } from '@/components/auth/SocialProviderIcon';
import { Divider } from '@/components/ui/primitives';

/** The providers, in the order their buttons appear. */
const PROVIDERS: readonly SocialProvider[] = ['google', 'apple'];

/**
 * SocialOAuthButtons — "Continue with Google" and "Continue with Apple" for
 * the log-in and register pages, and for confirming it's you.
 *
 * One label on every page: whichever page the user starts on, a provider signs
 * in to an account that exists and asks before making one that doesn't. The
 * buttons are the same size, so Apple's is as prominent as Google's.
 * Confirming it's you offers only the account's own provider (`providers`),
 * as "Sign in with" (`wording`).
 */
export function SocialOAuthButtons({
  onProviderClick,
  providers = PROVIDERS,
  disabled = false,
  wording = 'continue',
  className = '',
  showDivider = true,
}: SocialOAuthButtonsProps): React.JSX.Element {
  const offered = PROVIDERS.filter((provider) => providers.includes(provider));

  return (
    <div className={`w-full ${className}`.trim()}>
      {showDivider && (
        <Divider label="or" labelPosition="center" className="my-6" />
      )}

      <div className="flex flex-col gap-3">
        {offered.map((provider) => (
          <button
            key={provider}
            type="button"
            disabled={disabled}
            onClick={() => onProviderClick?.(provider)}
            className="flex h-12 w-full items-center justify-center gap-3 rounded-lg border border-stroke-default bg-transparent text-label-md font-medium text-neutral-300 transition-colors hover:border-stroke-strong hover:bg-lift-3 hover:text-neutral-100 focus:outline-none focus-visible:ring-state-focus disabled:cursor-not-allowed disabled:opacity-40"
          >
            <SocialProviderIcon provider={provider} className="h-5 w-5" />
            {SOCIAL_BUTTON_WORDS[wording]} {SOCIAL_PROVIDER_NAMES[provider]}
          </button>
        ))}
      </div>
    </div>
  );
}

export default SocialOAuthButtons;
